'use client';

import { faArrowRightLong } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Typography } from '@mui/material';
import React from 'react';

import { Button } from '@/components/atoms';
import { SectionContainer } from '@/components/layouts/SectionContainer';
import { CTA, StrapiImage } from '@/services';
import { getImageUrl } from '@/services/utils';

/**
 * Service Section Component
 *
 * Displays a two-column layout with title/description on the left and service cards on the right.
 * Based on the Figma design with three main service categories.
 *
 * @param services - Array of service data
 * @param title - Section title
 * @param subtitle - Optional subtitle
 * @param cta - CTA button configuration from backend
 * @param backgroundImage - Background image for the section
 */
export interface Service {
  id: number; // Changed to number to match backend API
  title: string;
  description: string;
  image?: any; // Can be string or object
  buttonText?: string;
  buttonLink?: string;
}

export interface ServiceSectionProps {
  services: Service[];
  title: string;
  subtitle?: string;
  cta?: CTA;
  backgroundImage?: StrapiImage | null;
}

const ServiceSection = ({
  services,
  title,
  subtitle,
  cta,
  backgroundImage,
}: ServiceSectionProps) => {
  // Fallback CTA data if not provided from backend
  const fallbackCTA: CTA = {
    id: 1,
    title: 'Tìm hiểu thêm',
    description: 'Khám phá các dịch vụ của chúng tôi',
    buttonText: 'TÌM HIỂU THÊM',
    buttonLink: '/services',
    backgroundImage: {
      id: 1,
      name: 'default-cta-bg',
      url: '/service-section-background.jpg',
      width: 1920,
      height: 1080,
      alternativeText: null,
      caption: null,
    },
  };

  // Use CTA from backend or fallback
  const ctaData = cta || fallbackCTA;

  // Track hover state for each service card
  const [hoveredCard, setHoveredCard] = React.useState<number | null>(null);

  // Slice services to show only 3
  const visibleServices = services.slice(0, 3);

  return (
    <SectionContainer>
      {backgroundImage && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '-345px',
            right: 0,
            height: '600px', // crop height
            backgroundImage: `url(${getImageUrl(backgroundImage)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            zIndex: -1, // keep it behind content
          }}
        />
      )}
      <Box
        component='section'
        id='service-section'
        data-component='service-section'
        sx={{
          width: '100%',
          minHeight: '680px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            width: '100%',
          }}
        >
          {/* Left Column - Title and Description */}
          <Box
            id='service-section-left-column'
            sx={{
              flex: { xs: 'none', md: '0 0 33.333%' },
              height: { xs: 'auto', md: '680px' },
              py: { xs: 4, md: 0 },
              pr: '30px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {/* Section Header */}
            <Box id='service-section-header' sx={{ mb: { xs: 4, md: 6 } }}>
              <Typography
                id='service-section-title'
                variant='h2'
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '28px', md: '36px' },
                  lineHeight: 1.53,
                  color: '#001A44',
                  mb: 2,
                  textTransform: 'uppercase',
                  position: 'relative',
                  width: 'fit-content',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-8px',
                    left: 0,
                    width: '100%',
                    height: '3px',
                    backgroundColor: '#EB722E',
                  },
                }}
              >
                {title}
              </Typography>

              {subtitle && (
                <Typography
                  id='service-section-subtitle'
                  variant='body1'
                  sx={{
                    fontSize: '16px',
                    color: '#555555',
                    maxWidth: { xs: '100%', md: '600px' },
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
            {/* Main CTA Button */}
            <Box id='service-section-cta-container'>
              <Button
                id='service-section-main-cta'
                variant='primary'
                href={ctaData.buttonLink}
                icon={
                  <FontAwesomeIcon
                    icon={faArrowRightLong}
                    style={{ width: '16px', height: '16px' }}
                  />
                }
                iconPosition='end'
                size='large'
                sx={{
                  backgroundColor: '#FCE018',
                  color: '#001A44',
                  fontSize: '18px',
                  fontWeight: 700,
                  fontFamily: 'Sarabun, sans-serif',
                  px: 4,
                  py: 2,
                }}
              >
                {ctaData.buttonText}
              </Button>
            </Box>
          </Box>

          {/* Right Column - Service Cards */}
          <Box
            id='service-section-right-column'
            sx={{
              flex: { xs: 'none', md: '0 0 66.667%' },
              height: '680px',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            {/* Service Cards Container */}
            <Box
              id='service-section-cards-container'
              sx={{
                height: '100%',
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(3, 1fr)',
                  sm: 'repeat(3, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(3, 1fr)',
                },
                gap: 0,
                overflow: 'hidden',
              }}
            >
              {visibleServices.map((service, index) => (
                <Box
                  key={service.id}
                  id={`service-card-${service.id}`}
                  data-service-index={index}
                  onMouseEnter={() => setHoveredCard(service.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRight:
                      index < services.length - 1
                        ? '1px solid rgba(255, 255, 255, 0.2)'
                        : 'none',
                  }}
                >
                  {/* Background Image */}
                  {service.image && (
                    <Box
                      id={`service-card-${service.id}-background`}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: `url(${getImageUrl(service.image)})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        zIndex: 1,
                      }}
                    />
                  )}

                  {/* Gradient Overlay */}
                  <Box
                    id={`service-card-${service.id}-overlay`}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        hoveredCard === service.id
                          ? 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 50%, rgba(0, 0, 0, 0.8) 100%)'
                          : 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 0.8) 100%)',
                      zIndex: 2,
                      transition: 'background 0.3s ease-in-out',
                    }}
                  />

                  {/* Content */}
                  <Box
                    id={`service-card-${service.id}-content`}
                    sx={{
                      position: 'relative',
                      zIndex: 3,
                      height: '100%',
                      color: 'white',
                      textAlign: 'left',
                      padding: '24px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    {/* Service Title */}
                    <Typography
                      id={`service-card-${service.id}-title`}
                      variant='h3'
                      sx={{
                        fontSize: '20px',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        textAlign: 'center',
                        width: '100%',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        mb: 3,
                        transform:
                          hoveredCard === service.id
                            ? 'translateY(-20px)'
                            : 'translateY(0)',
                        opacity: hoveredCard === service.id ? 0.9 : 1,
                      }}
                    >
                      {service.title}
                    </Typography>

                    {/* Service Description - Conditionally rendered */}
                    <Typography
                      id={`service-card-${service.id}-description`}
                      variant='body2'
                      sx={{
                        fontSize: '14px',
                        lineHeight: 1.4,
                        mb: 2,
                        opacity: hoveredCard === service.id ? 1 : 0,
                        transform:
                          hoveredCard === service.id
                            ? 'translateY(0)'
                            : 'translateY(10px)',
                        transition: 'all 0.3s ease-in-out',
                        pointerEvents:
                          hoveredCard === service.id ? 'auto' : 'none',
                        height: hoveredCard === service.id ? 'auto' : 0,
                        overflow: 'hidden',
                      }}
                    >
                      {service.description}
                    </Typography>

                    {/* Learn More Button - Conditionally rendered */}
                    <Button
                      id={`service-card-${service.id}-button`}
                      variant='text'
                      size='small'
                      href={service.buttonLink}
                      icon={
                        <FontAwesomeIcon
                          icon={faArrowRightLong}
                          style={{ width: '16px', height: '16px' }}
                        />
                      }
                      iconPosition='end'
                      sx={{
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 600,
                        fontFamily: 'Sarabun, sans-serif',
                        opacity: hoveredCard === service.id ? 1 : 0,
                        transform:
                          hoveredCard === service.id
                            ? 'translateY(0)'
                            : 'translateY(10px)',
                        transition: 'all 0.3s ease-in-out',
                        pointerEvents:
                          hoveredCard === service.id ? 'auto' : 'none',
                        height: hoveredCard === service.id ? 'auto' : 0,
                        overflow: 'hidden',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          transform:
                            hoveredCard === service.id
                              ? 'scale(1.02)'
                              : 'translateY(10px)',
                        },
                      }}
                    >
                      {service.buttonText}
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </SectionContainer>
  );
};

export default ServiceSection;
